import { Anchor, Center, Loader } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  useMutation,
  type UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { env } from 'env';
import Cookies from 'js-cookie';
import { CHAT_BASE_URL } from 'lib/constants/chat-url';
import posthog from 'posthog-js';
import type React from 'react';
import {
  createContext,
  type JSX,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  matchPath,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router';
import { routes } from 'routes/routes';
import {
  OTP_ATTEMPTS_EXCEEDED_CODE,
  type SessionUser,
  type User,
  type UserProject,
} from 'types/auth';

import { KapaMessage } from '@/common/kapa-message';

import { captureEvent } from './analytics/capture-event';
import { HttpError } from './errors';
import { buildRecaptchaHeaders, RecaptchaAction } from './recaptcha';

export const DEFAULT_NEXT_URL = `/`;

const ACCEPT_INVITE_PATH = '/auth/accept-invite';
const ONBOARDING_PATH = routes.onboarding.build();

// Extend the Window interface to include the kapaSettings object w/ user's email, set by auth provider
declare global {
  interface Window {
    kapaSettings?: {
      user?: {
        uniqueClientId?: string;
        email?: string;
      };
    };
  }
}

/** What the session endpoints answer with, before the team is known to exist. */
interface SessionResponse {
  user: SessionUser;
}

interface LoginResponse {
  user: User;
}

interface LogoutResponse {
  message: string;
}

interface GenerateOTPResponse {
  message: string;
}

interface ProjectState {
  availableProjects: UserProject[];
}

/**
 * Explicit navigation target for a project switch (drill-down, URL sync). When
 * omitted, only the `:project` segment is swapped in place.
 */
interface ProjectNavDestination {
  path?: string;
  search?: string;
}

const projectSectionRoutes = {
  'get-started': routes.getStarted,
  conversations: routes.conversations,
  'coverage-gaps': routes.coverageGaps,
  sources: routes.sources,
  integrations: routes.integrations,
  customizations: routes.customizations,
  'source-analytics': routes.sourceAnalytics,
  'source-groups': routes.sourceGroups,
  tags: routes.tags,
  users: routes.users,
  'api-keys': routes.apiKeys,
  'top-questions': routes.topQuestions,
  playground: routes.playground,
} as const;

/** The project fields the edit modal can change locally. */
type ProjectEditableFields = Partial<
  Pick<UserProject, 'project_name' | 'is_internal'>
>;

type ProjectAction =
  | {
      type: 'INITIALIZE';
      payload: {
        projects: UserProject[];
        currentProjectId: string | undefined;
      };
    }
  | { type: 'CHANGE_PROJECT_ORDER'; payload: string[] }
  | {
      type: 'EDIT_PROJECT';
      payload: { id: string; changes: ProjectEditableFields };
    };

type ProjectReducer = {
  selectProject: (
    projectId: string,
    destination?: ProjectNavDestination,
  ) => void;
  /** Active project, derived from the `:project` URL segment. */
  selected: UserProject | null;
} & ProjectState;

/**
 * A project the session can actually open. The team's project list includes ones
 * the user has no read permission on, so anything picking a project to land on —
 * or to report — has to filter first.
 */
export const isReadableProject = (project: UserProject) =>
  project.permissions.all.includes('read_project');

export const getSafeNextUrl = (next: string) => {
  // Allow relative paths and absolute paths within the kapa-service origin
  if (
    next.startsWith('/') ||
    new URL(next).origin === new URL(env.VITE_KAPA_SERVICE_URL).origin
  ) {
    return next;
  }

  // Otherwise, fallback to the default
  console.error('Invalid next URL, using default', next);
  return DEFAULT_NEXT_URL;
};

export type AuthFetch = (
  url: string,
  fetchObject?: RequestInit,
  unsetContentType?: boolean,
) => Promise<Response>;

export interface AuthContextValue {
  /** Narrowed to a session with a team, which every routed screen renders against. */
  user: LoginResponse | null;
  /** The raw session, for a component mounted outside the router. */
  session: SessionResponse | null;
  project: ProjectReducer;
  generateOTP: UseMutationResult<GenerateOTPResponse, unknown, string, unknown>;
  verifyOTP: UseMutationResult<
    SessionResponse,
    unknown,
    { email: string; otp: string },
    unknown
  >;
  logout: UseMutationResult<LogoutResponse, unknown, void, unknown>;
  authFetch: AuthFetch;
  /** Seat a session the caller already holds, saving a second round trip. */
  hydrateSession: (user: SessionUser) => void;
  changeProjectOrder: (newOrder: string[]) => void;
  editProject: (id: string, changes: ProjectEditableFields) => void;
  /** The last code has used up its guesses; only a new code moves forward. */
  otpAttemptsExhausted: boolean;
}

export const AuthContext = createContext<AuthContextValue>(
  {} as AuthContextValue,
);

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * This component provides the user object and functions for
 * generating an OTP, verifying an OTP, and logging out.
 *
 * @param {AuthProviderProps} props - the component props
 * @returns {JSX.Element}
 */
const AuthProvider = (props: AuthProviderProps): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<SessionResponse | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  // A null `user` means either "not fetched yet" or "signed out", and the /me
  // effect must only act on the first. Without this it would re-fetch after a
  // logout whose request failed, restore the still-valid session, and undo the
  // local sign-out.
  const hasLoggedOut = useRef(false);

  // Project logic
  function _projectReducer(
    state: ProjectState,
    action: ProjectAction,
  ): ProjectState {
    switch (action.type) {
      // Initialize the project reducer state. This always sets the
      // available projects and the selected project. The selected
      // project is determined by the router URL or is set to the first
      // available project if the router does not include a project ID.
      // If there are no available projects, the selected project
      // is null. This would be a team member with no projects.
      case 'INITIALIZE': {
        const { projects: allProjects, currentProjectId } = action.payload;
        const projects = (allProjects ?? []).filter(isReadableProject);

        // On a project route whose id we can't read, redirect to the default
        // project. `selected` itself is derived from the URL, not stored here.
        const hasCurrentProject = projects.some(
          (project) => project.id === currentProjectId,
        );
        const firstProject = projects[0];
        if (currentProjectId && !hasCurrentProject && firstProject) {
          navigate(routes.dashboard.build({ project: firstProject.id }));
        }

        return { ...state, availableProjects: projects };
      }
      // Change the order of the available projects. This is used
      // when the user drags and drops projects to reorder them.
      case 'CHANGE_PROJECT_ORDER':
        // payload is an array of project IDs in the new order
        return {
          ...state,
          availableProjects: state.availableProjects.sort((a, b) => {
            const aIndex = action.payload.indexOf(a.id);
            const bIndex = action.payload.indexOf(b.id);
            return aIndex - bIndex;
          }),
        };
      case 'EDIT_PROJECT':
        return {
          ...state,
          availableProjects: state.availableProjects.map((project) =>
            project.id === action.payload.id
              ? { ...project, ...action.payload.changes }
              : project,
          ),
        };
      default:
        return state;
    }
  }

  const [projectReducerState, projectReducerDispatch] = useReducer(
    _projectReducer,
    {
      availableProjects: [],
    },
  );
  // Read the latest pathname from a ref so it stays out of selectProject's deps.
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const selectProject = useCallback(
    (projectId: string, destination?: ProjectNavDestination) => {
      const target = projectReducerState.availableProjects.find(
        (p) => p.id === projectId,
      );
      if (!target) return;
      // Preserve the current section (swap only :project) unless the caller
      // passes an explicit path; deeper ids (thread, source) don't carry across.
      const section = matchPath('/:project/:section/*', pathnameRef.current)
        ?.params.section;
      const dashboardPath = routes.dashboard.build({ project: target.id });
      const sectionRoute = section
        ? projectSectionRoutes[section as keyof typeof projectSectionRoutes]
        : undefined;
      const path =
        destination?.path ??
        (sectionRoute
          ? sectionRoute
              .build({ project: target.id })
              .slice(dashboardPath.length)
          : '');
      // Navigate only; `selected` is derived from the resulting URL.
      navigate(`${dashboardPath}${path}${destination?.search ?? ''}`);
    },
    [projectReducerState.availableProjects, navigate],
  );

  // `selected` is derived from the `:project` URL segment, never stored, so it
  // can't lag the pathname (which would blink the active nav highlight during a
  // redirect) and Back/Forward update it for free. The ref keeps the last
  // resolved project so standalone routes (no `:project`) still report one.
  const lastSelectedProjectRef = useRef<UserProject | null>(null);
  const selectedProject = useMemo(() => {
    const projects = projectReducerState.availableProjects;
    const routeProjectId = matchPath('/:project/*', location.pathname)?.params
      ?.project;
    const match = routeProjectId
      ? projects.find((p) => p.id === routeProjectId)
      : undefined;
    return match ?? lastSelectedProjectRef.current ?? projects[0] ?? null;
  }, [location.pathname, projectReducerState.availableProjects]);
  useEffect(() => {
    lastSelectedProjectRef.current = selectedProject;
  }, [selectedProject]);

  const project = useMemo(
    () => ({
      availableProjects: projectReducerState.availableProjects,
      selected: selectedProject,
      selectProject,
    }),
    [projectReducerState.availableProjects, selectedProject, selectProject],
  );

  const hydrateSession = useCallback((sessionUser: SessionUser) => {
    hasLoggedOut.current = false;
    setUser({ user: sessionUser });
    projectReducerDispatch({
      type: 'INITIALIZE',
      payload: {
        projects: sessionUser.team?.projects ?? [],
        currentProjectId: undefined,
      },
    });
  }, []);

  // This is the function that processes the user logout in the
  // frontend. It removes the user from local storage, resets the
  // user state, and invalidates all queries. It then redirects
  // the user to the login page. We separate this from the logout
  // mutation because we need to be able to do this without calling
  // the logout mutation (e.g. when the session is invalid).
  //
  // When `nextUrl` is provided, it is forwarded to the signin page
  // as a `?next=` query param (via getSafeNextUrl, which blocks
  // open-redirect targets). The initial auth check passes the
  // current path so deep-links survive the sign-in bounce;
  // explicit logout flows omit it so deliberately-logged-out
  // users don't get sent back to where they were.
  const _processLogout = useCallback(
    async (nextUrl?: string) => {
      const safeNext = nextUrl ? getSafeNextUrl(nextUrl) : DEFAULT_NEXT_URL;
      const target =
        safeNext && safeNext !== DEFAULT_NEXT_URL
          ? `/auth/signin?next=${encodeURIComponent(safeNext)}`
          : '/auth/signin';
      hasLoggedOut.current = true;
      setUser(null);
      navigate(target);
      // Drop the cache at the identity boundary so a following sign-in on the
      // same tab never renders the previous user's retained entries.
      queryClient.clear();
    },
    [queryClient, navigate],
  );

  // This is the fetch function that makes the request to log the user out.
  // This deletes the session in the backend database.
  const _logout = useCallback(async (): Promise<LogoutResponse> => {
    const res = await fetch(`${env.VITE_KAPA_SERVICE_URL}/org/v1/logout/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
    if (!res.ok) {
      throw new Error(res.statusText);
    }
    return res.json() as Promise<LogoutResponse>;
  }, []);

  const logout = useMutation({
    mutationFn: _logout,
    onSuccess: async () => {
      // process the logout in the frontend
      await _processLogout();
    },
    onError: async () => {
      // also process logout in the frontend if logout fails
      console.error('Error logging out');
      await _processLogout();
    },
  });

  const safeRedirectToNextUrl = useCallback(
    (next: string) => {
      const safeNextUrl = getSafeNextUrl(next);

      if (safeNextUrl.startsWith('/')) {
        navigate(safeNextUrl);
      } else {
        window.location.replace(safeNextUrl);
      }
    },
    [navigate],
  );

  // Initialize the user and project reducer state. The user object is
  // fetched from the kapa-sercice /me endpoint on app load. If the user
  // object fetch fails, the user will be logged out. This acts
  // as our main authentication check. If the user is not logged in,
  // they are redirected to the login page. If the user cannot authenticate,
  // they are logged out automatically and redirected to the login
  // page on any authFetch request that returns a 401/403 response (see
  // implementation below).
  useEffect(() => {
    // In React Router, we don't need to check if router is ready
    // The router is always ready
    if (user || hasLoggedOut.current) {
      return;
    }

    const isSigningIn = () => {
      return location.pathname.startsWith('/auth');
    };

    // The invite page is the one /auth route a signed-in visitor still has to
    // land on, so it opts out of the post-sign-in redirect while keeping the
    // logged-out exemption every /auth route gets.
    const isAcceptingInvite = () => {
      return location.pathname === ACCEPT_INVITE_PATH;
    };

    const getUser = async () => {
      const res = await fetch(`${env.VITE_KAPA_SERVICE_URL}/org/v1/me/`, {
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error(res.statusText);
      }
      return res.json() as Promise<SessionUser>;
    };

    /**
     * Updates the user object via the /me endpoint from the server.
     */
    const setUserAndProjects = async () => {
      try {
        const user = await getUser();
        const newUserData = {
          user,
        };

        if (typeof window !== 'undefined' && user.email) {
          // Set the user's email on the window object, preserving any existing settings
          window.kapaSettings = {
            ...window.kapaSettings,
            user: {
              ...window.kapaSettings?.user,
              email: user.email,
            },
          };
        }

        // Use matchPath since AuthProvider is outside the Route tree (useParams won't work)
        // Don't match paths that are standalone routes (not project-scoped)
        const isStandaloneRoute =
          location.pathname.startsWith('/settings') ||
          location.pathname.startsWith('/oauth') ||
          location.pathname.startsWith('/thread') ||
          location.pathname.startsWith(ONBOARDING_PATH);
        const projectMatch = isStandaloneRoute
          ? null
          : matchPath('/:project/*', location.pathname);
        projectReducerDispatch({
          type: 'INITIALIZE',
          payload: {
            projects: newUserData.user.team?.projects ?? [],
            currentProjectId: projectMatch?.params?.project,
          },
        });
        setUser(newUserData);

        if (isSigningIn() && !isAcceptingInvite()) {
          if (user.team) {
            safeRedirectToNextUrl(searchParams.get('next') || DEFAULT_NEXT_URL);
          } else {
            navigate(ONBOARDING_PATH, { replace: true });
          }
        }
      } catch {
        // Only process logout on the client if the user is
        // currently not trying to sign in routes. Otherwise silently
        // fail and let user log in.
        if (!isSigningIn()) {
          await _processLogout(location.pathname + location.search);
        }
      } finally {
        setIsAuthLoading(false);
      }
    };

    // grab user data from local storage if it exists
    void setUserAndProjects();
    // Given that we need the location.pathname and navigate
    // functions, we need to add them to the dependency array. However,
    // this makes this useEffect run on every navigation change. To
    // work around this, we check if we've already set the user/project and if
    // we have, we don't run the effect again. Need to include _processLogout
    // in dependency array as this is the react best practice to avoid stale
    // data referencing.
  }, [
    project.selected,
    location.pathname,
    location.search,
    navigate,
    user,
    _processLogout,
    searchParams,
    safeRedirectToNextUrl,
  ]);

  // Login logic
  // =====================

  // This is the fetch function that makes the request to generate an OTP.
  // We're using the normal fetch here because we don't have a user yet.
  const _generateOTP = async (email: string) => {
    const captchaHeaders = await buildRecaptchaHeaders(
      RecaptchaAction.LoginOtpGenerate,
    );
    const res = await fetch(
      `${env.VITE_KAPA_SERVICE_URL}/org/v1/otp-login/generate/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...captchaHeaders,
        },
        body: JSON.stringify({ email }),
      },
    );
    if (!res.ok) {
      const responseBody = await res.text();
      throw new HttpError(res, responseBody, 'generate_otp');
    }
    return res.json();
  };
  const generateOTP = useMutation({
    mutationFn: _generateOTP,

    onSuccess: async (data, email) => {
      if (data) {
        posthog?.capture('signin_code_requested');
        // Preserve the query parameters to keep any ?next= query parameter
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('email', email);
        navigate(`/auth/verify-otp?${nextSearchParams.toString()}`);
      }
    },

    // A refused code request is reported on the email field by the sign-in page,
    // which owns the form the user is looking at, so nothing is raised as a
    // toast here.
    onError: (error: unknown) => {
      captureEvent('signin_code_request_failed', {
        status: error instanceof HttpError ? error.status : undefined,
      });
      console.error('Error generating OTP', error);
    },
  });

  // This is the fetch function that makes the request to verify an OTP.
  // We're using the normal fetch here because we don't have a user yet.
  const _verifyOTP = async ({
    email,
    otp,
  }: {
    email: string;
    otp: string;
  }): Promise<SessionResponse> => {
    const res = await fetch(
      `${env.VITE_KAPA_SERVICE_URL}/org/v1/otp-login/verify/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // important in order to set cookies on the client
        body: JSON.stringify({ email, otp }),
      },
    );
    if (!res.ok) {
      const responseBody = await res.text();
      throw new HttpError(res, responseBody, 'verify_otp');
    }
    return res.json() as Promise<SessionResponse>;
  };
  const verifyOTP = useMutation({
    mutationFn: _verifyOTP,

    onSuccess: async (data) => {
      if (data) {
        posthog?.capture('signin_code_verified');
        hydrateSession(data.user);
        safeRedirectToNextUrl(searchParams.get('next') || DEFAULT_NEXT_URL);
      }
    },

    onError: (error: HttpError) => {
      console.error('Error verifying OTP', error);

      // Try to parse backend error message
      let backendMessage: string | undefined;
      let errorCode: string | undefined;
      try {
        const errorData = JSON.parse(error.responseBody) as {
          message?: string;
          error?: string;
          code?: string;
        };
        backendMessage = errorData.message || errorData.error;
        errorCode = errorData.code;
      } catch {
        // Response body isn't JSON, ignore
      }

      posthog?.capture('signin_code_rejected', {
        status: error.status,
        attempts_exhausted: errorCode === OTP_ATTEMPTS_EXCEEDED_CODE,
      });

      // A rejected code is reported next to the input on the verify page, so it
      // is deliberately not raised as a toast too. Rate limiting is, since that
      // is about the request rather than the code that was typed.
      if (error.status === 429) {
        notifications.show({
          title: 'Too Many Requests',
          message:
            backendMessage ||
            'You have made too many attempts. Please wait and try again after one minute.',
          color: 'yellow',
        });
      }
    },
  });

  // Whether the last verification attempt exhausted the code's allowance of
  // wrong guesses, in which case the only way forward is a new code.
  const otpAttemptsExhausted = ((): boolean => {
    const error = verifyOTP.error;
    if (!error) return false;
    try {
      const errorData = JSON.parse(error.responseBody) as { code?: string };
      return errorData.code === OTP_ATTEMPTS_EXCEEDED_CODE;
    } catch {
      return false;
    }
  })();

  const refreshCSRFCookie = async () => {
    const res = await fetch(
      `${env.VITE_KAPA_SERVICE_URL}/org/v1/csrf/refresh`,
      {
        credentials: 'include', // important in order to set cookies on the client
      },
    );
    if (!res.ok) {
      throw new Error(res.statusText);
    }
  };

  /**
   * Tries to get the CSRF token from the cookie and tries refreshing
   * the token one time if it is not present. If the refresh fails
   * the user is logged out.
   */
  const getCSRFToken = useCallback(async () => {
    const csrfToken = Cookies.get('csrftoken');
    if (csrfToken) {
      return csrfToken;
    }

    try {
      await refreshCSRFCookie();
      const csrfToken = Cookies.get('csrftoken');
      if (!csrfToken) {
        throw new Error('CSRF token not set in cookies');
      }
      return csrfToken;
    } catch (e) {
      // If the CSRF token cannot be refreshed, try to fully log the user out.
      await _logout();
      throw new Error('Failed to obtain CSRF token', { cause: e });
    }
  }, [_logout]);

  /**
   * This function is a wrapper around the fetch function that
   * adds the user's credentials as well as the CSRF token to the request headers.
   * If the CSRF token cannot be obtained after a retry, the user is logged out.
   *
   * @param {string} url - the url to fetch
   * @param {RequestInit} fetchObject - the fetch object
   * @param {boolean} unsetContentType - whether to unset the content type
   * @returns {Promise<Response>} the fetch response
   */
  const authFetch = useCallback(
    async (
      url: string,
      fetchObject: RequestInit = {},
      unsetContentType = false,
    ): Promise<Response> => {
      // grab user from local storage
      const csrfToken = await getCSRFToken();

      const headers: RequestInit['headers'] = {
        'X-CSRFToken': csrfToken,
      };

      if (!unsetContentType) {
        headers['Content-Type'] = 'application/json';
      }

      // add CSRF token to fetch object
      fetchObject.headers = {
        ...headers,
        ...fetchObject.headers,
      };

      fetchObject.credentials = 'include';

      // make the fetch request
      if (!url.startsWith('http')) {
        if (url.startsWith('/')) {
          url = `${env.VITE_KAPA_SERVICE_URL}${url}`;
        } else {
          url = `${env.VITE_KAPA_SERVICE_URL}/${url}`;
        }
      }
      const response = await fetch(url, fetchObject);

      // if the the session is invalid invalid,
      // log the user out on the frontend.
      if (response.status === 401) {
        await _processLogout();
      }

      if (response.status === 403) {
        // TODO: show notification
      }

      return response;
    },
    [_processLogout, getCSRFToken],
  );

  // This function is used to change the order of the available projects.
  // This is used when the user drags and drops projects to reorder them.
  const changeProjectOrder = (newOrder: string[]) => {
    projectReducerDispatch({ type: 'CHANGE_PROJECT_ORDER', payload: newOrder });
  };

  const editProject = (id: string, changes: ProjectEditableFields) => {
    projectReducerDispatch({
      type: 'EDIT_PROJECT',
      payload: { id, changes },
    });
  };

  // Show a loading spinner while checking authentication status.
  // Skip this for auth pages since they don't require authentication.
  const isAuthPage = location.pathname.startsWith('/auth');
  const isOAuthCallback = location.pathname === '/auth';
  if (isAuthLoading && (!isAuthPage || isOAuthCallback)) {
    return (
      <Center h="100vh">
        <Loader size="lg" variant="dots" color="primary" />
      </Center>
    );
  }

  // Onboarding is the only screen a teamless session may render. /auth stays
  // reachable so such an account can still accept an invite.
  if (
    user &&
    !user.user.team &&
    !isAuthPage &&
    location.pathname !== ONBOARDING_PATH
  ) {
    return <Navigate to={ONBOARDING_PATH} replace />;
  }

  if (user?.user.team && location.pathname === ONBOARDING_PATH) {
    return <Navigate to={DEFAULT_NEXT_URL} replace />;
  }

  // If the user has no projects with at least read permissions,
  // render a message to the user that they have no projects.
  if (user?.user.team && project.availableProjects.length === 0) {
    return (
      <KapaMessage
        title="You don't have access to the Kapa management platform."
        message={
          <>
            If you&apos;re looking to chat with Kapa, go to the Internal
            Technical Assistant at{' '}
            <Anchor href={CHAT_BASE_URL} target="_blank" rel="noreferrer">
              chat.kapa.ai
            </Anchor>
            . For access to this platform, please contact your team
            administrator.
          </>
        }
      />
    );
  }

  return (
    <AuthContext.Provider
      value={{
        // Narrowed for the guarded routes; `/onboarding` and `/auth/*` render a
        // teamless session and must read `session`.
        user: user as LoginResponse | null,
        session: user,
        generateOTP,
        verifyOTP,
        logout,
        authFetch,
        hydrateSession,
        project,
        changeProjectOrder,
        editProject,
        otpAttemptsExhausted,
      }}
      {...props}
    />
  );
};

const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth };
